import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Input from '@components/common/Input';
import CurrencyInput from '@components/common/CurrencyInput';
import Select from '@components/common/Select';
import Button from '@components/common/Button';
import { useAuth } from '@hooks/useAuth';
import { ACCOUNT_TYPE_LABELS, COLORS } from '@utils/constants';

const accountSchema = z.object({
  name: z.string().min(2, 'Nome deve ter no minimo 2 caracteres'),
  type: z.string().min(1, 'Tipo e obrigatorio'),
  initialBalance: z.number().default(0),
  color: z.string().default('#3B82F6'),
  icon: z.string().default('🏦'),
  visibility: z.string().default('shared'),
});

const accountTypeOptions = Object.entries(ACCOUNT_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const icons = ['🏦', '💳', '💰', '📈', '💵', '🏧', '💜', '🧡', '💛', '💚', '💙', '🖤'];

const visibilityOptions = [
  { value: 'shared', label: 'Compartilhada (todos veem)' },
  { value: 'personal', label: 'Pessoal (so voce ve)' },
];

const AccountForm = ({ account, onSubmit, onCancel, isLoading }) => {
  const { tenant } = useAuth();
  const isFamily = tenant?.plan === 'family';

  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(accountSchema),
    defaultValues: {
      name: '',
      type: 'checking',
      initialBalance: 0,
      color: '#3B82F6',
      icon: '🏦',
      visibility: 'shared',
    },
  });

  const selectedColor = watch('color');
  const selectedIcon = watch('icon');

  useEffect(() => {
    if (account) {
      reset({
        name: account.name,
        type: account.type,
        initialBalance: account.initialBalance || 0,
        color: account.color || '#3B82F6',
        icon: account.icon || '🏦',
        visibility: account.visibility || 'shared',
      });
    }
  }, [account, reset]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Input
        label="Nome da conta"
        placeholder="Ex: Nubank, Inter, etc"
        error={errors.name?.message}
        {...register('name')}
      />

      <Select
        label="Tipo de conta"
        options={accountTypeOptions}
        error={errors.type?.message}
        {...register('type')}
      />

      <Controller
        name="initialBalance"
        control={control}
        render={({ field }) => (
          <CurrencyInput
            label="Saldo inicial"
            value={field.value}
            onValueChange={(values) => field.onChange(values.floatValue ?? 0)}
            onBlur={field.onBlur}
            allowNegative={true}
            error={errors.initialBalance?.message}
            hint="Informe o saldo atual da conta"
          />
        )}
      />

      {isFamily && (
        <Select
          label="Visibilidade"
          options={visibilityOptions}
          error={errors.visibility?.message}
          {...register('visibility')}
        />
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Icone
        </label>
        <div className="flex flex-wrap gap-2">
          {icons.map((icon) => (
            <button
              key={icon}
              type="button"
              onClick={() => setValue('icon', icon)}
              className={`w-10 h-10 rounded-lg border-2 text-xl flex items-center justify-center transition-colors ${
                selectedIcon === icon
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
              }`}
            >
              {icon}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Cor
        </label>
        <div className="flex flex-wrap gap-2">
          {COLORS.map((color) => (
            <button
              key={color.value}
              type="button"
              onClick={() => setValue('color', color.value)}
              className={`w-8 h-8 rounded-full border-2 transition-all ${
                selectedColor === color.value
                  ? 'border-gray-900 dark:border-white scale-110'
                  : 'border-transparent hover:scale-105'
              }`}
              style={{ backgroundColor: color.value }}
              title={color.name}
            />
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" isLoading={isLoading}>
          {account ? 'Salvar' : 'Criar Conta'}
        </Button>
      </div>
    </form>
  );
};

export default AccountForm;
